import React, { useState, useEffect } from 'react';
import {
  listRoles,
  getRole,
  createRole,
  updateRole,
  deleteRole,
  listPermissions,
  createPermission,
  updatePermission,
  deletePermission,
  getUserRoles,
  assignRole,
  removeUserRole,
  grantRolePermission,
  revokeRolePermission,
} from '../../api/roles';
import { listUsers } from '../../api/users';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Checkbox,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Group as GroupIcon,
  Assignment as AssignmentIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useMobileLayout } from '../../utils/useMobileLayout';

// Tab panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`role-tabpanel-${index}`}
      aria-labelledby={`role-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const RoleManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Roles state
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleDialog, setRoleDialog] = useState({ open: false, mode: 'create', role: null });

  // Role-details dialog (View Details → assign/revoke permissions).
  // `role` is the RoleWithPermissions payload from GET /rbac/roles/{id}.
  const [roleDetailDialog, setRoleDetailDialog] = useState({
    open: false,
    role: null,
    loading: false,
    busyPermId: null, // permission_id currently being granted/revoked
  });
  
  // Permissions state
  const [permissions, setPermissions] = useState([]);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [permissionDialog, setPermissionDialog] = useState({ open: false, mode: 'create', permission: null });
  
  // User roles state
  const [users, setUsers] = useState([]);
  const [userRoleDialog, setUserRoleDialog] = useState({ open: false, user: null });
  
  // Form state
  const [roleForm, setRoleForm] = useState({ name: '', description: '' });
  const [permissionForm, setPermissionForm] = useState({
    name: '',
    description: '',
    resource_type: '',
    action: '',
    conditions: {}
  });

  // Resource types and actions (must match backend ResourceType / ActionType enums)
  const resourceTypes = [
    'wildfire', 'user', 'image', 'video', 'weather',
    'tenant', 'firefighter', 'suburb', 'reporting', 'system',
    'estate', 'fpa_member', 'parcel', 'boundary', 'water_source',
  ];
  
  const actionTypes = [
    'create', 'read', 'update', 'delete', 'list', 
    'approve', 'export', 'import', 'moderate', 'manage'
  ];

  // Fetch data
  useEffect(() => {
    fetchRoles();
    fetchPermissions();
    fetchUsers();
  }, []);

  const fetchRoles = async () => {
    try {
      setIsLoading(true);
      const data = await listRoles();
      setRoles(data);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setError('Failed to load roles. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const data = await listPermissions();
      setPermissions(data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setError('Failed to load permissions. Please try again later.');
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await listUsers();
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Open the role-details dialog and load the role's current permissions.
  // The previous version of this function just returned the fetched role
  // and discarded it — clicking "View Details" looked broken because no
  // UI was wired to consume the result.
  const handleViewRoleDetails = async (role) => {
    setRoleDetailDialog({ open: true, role, loading: true, busyPermId: null });
    try {
      const full = await getRole(role.id);
      setRoleDetailDialog({ open: true, role: full, loading: false, busyPermId: null });
    } catch (err) {
      console.error('Error fetching role details:', err);
      setError(
        err?.response?.data?.detail
          || 'Failed to load role details. Please try again.',
      );
      setRoleDetailDialog({ open: false, role: null, loading: false, busyPermId: null });
    }
  };

  const reloadRoleDetail = async (roleId) => {
    try {
      const full = await getRole(roleId);
      setRoleDetailDialog((prev) =>
        prev.open ? { ...prev, role: full, busyPermId: null } : prev,
      );
    } catch (err) {
      console.error('Failed to reload role detail:', err);
      setRoleDetailDialog((prev) => ({ ...prev, busyPermId: null }));
    }
  };

  const handleGrantPermissionToRole = async (permissionId) => {
    if (!roleDetailDialog.role) return;
    const roleId = roleDetailDialog.role.id;
    setRoleDetailDialog((prev) => ({ ...prev, busyPermId: permissionId }));
    try {
      await grantRolePermission(roleId, permissionId);
      await reloadRoleDetail(roleId);
    } catch (err) {
      console.error('Failed to grant permission:', err);
      setError(
        err?.response?.data?.detail || 'Failed to grant permission to role.',
      );
      setRoleDetailDialog((prev) => ({ ...prev, busyPermId: null }));
    }
  };

  const handleRevokePermissionFromRole = async (permissionId) => {
    if (!roleDetailDialog.role) return;
    const roleId = roleDetailDialog.role.id;
    setRoleDetailDialog((prev) => ({ ...prev, busyPermId: permissionId }));
    try {
      await revokeRolePermission(roleId, permissionId);
      await reloadRoleDetail(roleId);
    } catch (err) {
      console.error('Failed to revoke permission:', err);
      setError(
        err?.response?.data?.detail || 'Failed to revoke permission from role.',
      );
      setRoleDetailDialog((prev) => ({ ...prev, busyPermId: null }));
    }
  };

  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Role management handlers
  const handleCreateRole = () => {
    setRoleForm({ name: '', description: '' });
    setRoleDialog({ open: true, mode: 'create', role: null });
  };

  const handleEditRole = (role) => {
    setRoleForm({ name: role.name, description: role.description || '' });
    setRoleDialog({ open: true, mode: 'edit', role });
  };

  const handleDeleteRole = async (role) => {
    if (window.confirm(`Are you sure you want to delete the role "${role.name}"?`)) {
      try {
        await deleteRole(role.id);
        fetchRoles();
      } catch (error) {
        console.error('Error deleting role:', error);
        setError('Failed to delete role. It may be assigned to users.');
      }
    }
  };

  const handleSaveRole = async () => {
    try {
      setIsLoading(true);
      if (roleDialog.mode === 'create') {
        await createRole(roleForm);
      } else {
        await updateRole(roleDialog.role.id, roleForm);
      }
      setRoleDialog({ open: false, mode: 'create', role: null });
      fetchRoles();
    } catch (error) {
      console.error('Error saving role:', error);
      setError('Failed to save role. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Permission management handlers
  const handleCreatePermission = () => {
    setPermissionForm({
      name: '',
      description: '',
      resource_type: '',
      action: '',
      conditions: {}
    });
    setPermissionDialog({ open: true, mode: 'create', permission: null });
  };

  const handleEditPermission = (permission) => {
    setPermissionForm({
      name: permission.name,
      description: permission.description || '',
      resource_type: permission.resource_type,
      action: permission.action,
      conditions: permission.conditions || {}
    });
    setPermissionDialog({ open: true, mode: 'edit', permission });
  };

  const handleDeletePermission = async (permission) => {
    if (window.confirm(`Are you sure you want to delete the permission "${permission.name}"?`)) {
      try {
        await deletePermission(permission.id);
        fetchPermissions();
      } catch (error) {
        console.error('Error deleting permission:', error);
        setError('Failed to delete permission. It may be assigned to roles.');
      }
    }
  };

  const handleSavePermission = async () => {
    try {
      setIsLoading(true);
      if (permissionDialog.mode === 'create') {
        await createPermission(permissionForm);
      } else {
        await updatePermission(permissionDialog.permission.id, permissionForm);
      }
      setPermissionDialog({ open: false, mode: 'create', permission: null });
      fetchPermissions();
    } catch (error) {
      console.error('Error saving permission:', error);
      setError('Failed to save permission. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // User role management
  const handleViewUserRoles = async (user) => {
    try {
      const data = await getUserRoles(user.id);
      setUserRoleDialog({ open: true, user: { ...user, roles: data } });
    } catch (error) {
      console.error('Error fetching user roles:', error);
      setError('Failed to load user roles.');
    }
  };

  const handleAssignRole = async (userId, roleId) => {
    try {
      await assignRole(userId, {
        user_id: userId,
        role_id: roleId
      });
      // Refresh user roles
      const user = users.find(u => u.id === userId);
      if (user) {
        handleViewUserRoles(user);
      }
    } catch (error) {
      console.error('Error assigning role:', error);
      setError('Failed to assign role to user.');
    }
  };

  const handleRevokeRole = async (userId, roleId) => {
    try {
      await removeUserRole(userId, roleId);
      // Refresh user roles
      const user = users.find(u => u.id === userId);
      if (user) {
        handleViewUserRoles(user);
      }
    } catch (error) {
      console.error('Error revoking role:', error);
      setError('Failed to revoke role from user.');
    }
  };

  if (isLoading && roles.length === 0 && permissions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Role & Permission Management
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper sx={{ width: '100%' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab icon={<GroupIcon />} label="Roles" />
            <Tab icon={<SecurityIcon />} label="Permissions" />
            <Tab icon={<AssignmentIcon />} label="User Roles" />
          </Tabs>

          {/* Roles Tab */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">System Roles</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateRole}
              >
                Create Role
              </Button>
            </Box>

            <Grid container spacing={3}>
              {roles.map((role) => (
                <Grid item xs={12} md={6} lg={4} key={role.id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6" component="div">
                          {role.name}
                        </Typography>
                        {role.is_system_role && (
                          <Chip label="System" size="small" color="secondary" sx={{ ml: 1 }} />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {role.description || 'No description provided'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Created: {new Date(role.created_at).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => handleViewRoleDetails(role)}
                      >
                        View Details
                      </Button>
                      {!role.is_system_role && (
                        <>
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleEditRole(role)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleDeleteRole(role)}
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </TabPanel>

          {/* Permissions Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">System Permissions</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreatePermission}
              >
                Create Permission
              </Button>
            </Box>

            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Resource</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {permissions.map((permission) => (
                    <TableRow key={permission.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {permission.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={permission.resource_type} size="small" color="primary" />
                      </TableCell>
                      <TableCell>
                        <Chip label={permission.action} size="small" color="secondary" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {permission.description || 'No description'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={permission.is_active ? 'Active' : 'Inactive'}
                          color={permission.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Edit Permission">
                          <IconButton
                            size="small"
                            onClick={() => handleEditPermission(permission)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Permission">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeletePermission(permission)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* User Roles Tab */}
          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" sx={{ mb: 2 }}>User Role Assignments</Typography>
            
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Legacy Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {(() => {
                          const userRoles = Array.isArray(user.roles) && user.roles.length
                            ? user.roles
                            : (user.role ? [user.role] : []);
                          if (userRoles.length === 0) {
                            return <Chip label="No Role" color="default" size="small" />;
                          }
                          return (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {userRoles.map((role) => {
                                const upper = String(role).toUpperCase();
                                const color =
                                  upper === 'SUPER_ADMIN' ? 'error' :
                                  upper === 'ADMIN'       ? 'secondary' :
                                  upper === 'STAFF'       ? 'primary' :
                                  'default';
                                return <Chip key={role} label={role} color={color} size="small" />;
                              })}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.is_approved ? 'Approved' : 'Pending'}
                          color={user.is_approved ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          startIcon={<ViewIcon />}
                          onClick={() => handleViewUserRoles(user)}
                        >
                          Manage Roles
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </Paper>

        {/* Role Dialog */}
        <Dialog
          open={roleDialog.open}
          onClose={() => setRoleDialog({ open: false, mode: 'create', role: null })}
          fullScreen={isMobile}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {roleDialog.mode === 'create' ? 'Create New Role' : 'Edit Role'}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Role Name"
              fullWidth
              variant="outlined"
              value={roleForm.name}
              onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={roleForm.description}
              onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRoleDialog({ open: false, mode: 'create', role: null })}>
              Cancel
            </Button>
            <Button onClick={handleSaveRole} variant="contained">
              {roleDialog.mode === 'create' ? 'Create' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Permission Dialog */}
        <Dialog
          open={permissionDialog.open}
          onClose={() => setPermissionDialog({ open: false, mode: 'create', permission: null })}
          fullScreen={isMobile}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {permissionDialog.mode === 'create' ? 'Create New Permission' : 'Edit Permission'}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Permission Name"
              fullWidth
              variant="outlined"
              value={permissionForm.name}
              onChange={(e) => setPermissionForm({ ...permissionForm, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              variant="outlined"
              value={permissionForm.description}
              onChange={(e) => setPermissionForm({ ...permissionForm, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Resource Type</InputLabel>
              <Select
                value={permissionForm.resource_type}
                label="Resource Type"
                onChange={(e) => setPermissionForm({ ...permissionForm, resource_type: e.target.value })}
              >
                {resourceTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Action</InputLabel>
              <Select
                value={permissionForm.action}
                label="Action"
                onChange={(e) => setPermissionForm({ ...permissionForm, action: e.target.value })}
              >
                {actionTypes.map((action) => (
                  <MenuItem key={action} value={action}>
                    {action.charAt(0).toUpperCase() + action.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPermissionDialog({ open: false, mode: 'create', permission: null })}>
              Cancel
            </Button>
            <Button onClick={handleSavePermission} variant="contained">
              {permissionDialog.mode === 'create' ? 'Create' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Role-details dialog — opens from "View Details" on a role card.
            Shows the role's currently-assigned permissions (revoke X) and
            the rest of the system's permissions (assign +). System roles
            are read-only here because the backend rejects modification on
            them with 400; we just hide the action buttons. */}
        <Dialog
          open={roleDetailDialog.open}
          onClose={() =>
            setRoleDetailDialog({ open: false, role: null, loading: false, busyPermId: null })
          }
          fullScreen={isMobile}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {roleDetailDialog.role?.name || 'Role'}
            {roleDetailDialog.role?.is_system_role && (
              <Chip
                label="System (read-only)"
                size="small"
                color="secondary"
                sx={{ ml: 1 }}
              />
            )}
          </DialogTitle>
          <DialogContent dividers>
            {roleDetailDialog.loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={28} />
              </Box>
            ) : roleDetailDialog.role ? (
              <>
                {roleDetailDialog.role.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {roleDetailDialog.role.description}
                  </Typography>
                )}

                {(() => {
                  const role = roleDetailDialog.role;
                  const readOnly = role.is_system_role;
                  const assignedIds = new Set((role.permissions || []).map((p) => p.id));
                  // Group every active system permission by `${resource}|${action}`
                  // so each matrix cell knows its candidate permission(s). Most
                  // cells map to one; read/update/delete also have an owner-scoped
                  // (_own) variant, rendered as a second "own" checkbox.
                  const byCell = {};
                  permissions.forEach((p) => {
                    if (p.is_active === false) return;
                    const key = `${p.resource_type}|${p.action}`;
                    (byCell[key] = byCell[key] || []).push(p);
                  });
                  return (
                    <>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Permissions ({role.permissions?.length || 0} granted)
                        {!readOnly && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            — tick to grant, untick to revoke
                          </Typography>
                        )}
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '60vh' }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{ position: 'sticky', left: 0, zIndex: 3, bgcolor: 'background.paper', fontWeight: 600 }}
                              >
                                Resource
                              </TableCell>
                              {actionTypes.map((action) => (
                                <TableCell
                                  key={action}
                                  align="center"
                                  sx={{ textTransform: 'capitalize', fontSize: '0.72rem', px: 0.5 }}
                                >
                                  {action}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {resourceTypes.map((resource) => (
                              <TableRow key={resource} hover>
                                <TableCell
                                  component="th"
                                  scope="row"
                                  sx={{
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 1,
                                    bgcolor: 'background.paper',
                                    fontWeight: 500,
                                    textTransform: 'capitalize',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {resource.replace(/_/g, ' ')}
                                </TableCell>
                                {actionTypes.map((action) => {
                                  const candidates = byCell[`${resource}|${action}`] || [];
                                  if (!candidates.length) {
                                    return (
                                      <TableCell key={action} align="center" sx={{ color: 'text.disabled', px: 0.5 }}>
                                        ·
                                      </TableCell>
                                    );
                                  }
                                  return (
                                    <TableCell key={action} align="center" sx={{ px: 0.5 }}>
                                      <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
                                        {candidates.map((perm) => {
                                          const granted = assignedIds.has(perm.id);
                                          const isOwn = String(perm.name || '').endsWith('_own');
                                          return (
                                            <Tooltip
                                              key={perm.id}
                                              title={`${perm.name || `${resource}_${action}`}${isOwn ? ' (owner-scoped)' : ''}`}
                                            >
                                              <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                                <Checkbox
                                                  size="small"
                                                  checked={granted}
                                                  disabled={readOnly || roleDetailDialog.busyPermId === perm.id}
                                                  onChange={() =>
                                                    granted
                                                      ? handleRevokePermissionFromRole(perm.id)
                                                      : handleGrantPermissionToRole(perm.id)
                                                  }
                                                  color={isOwn ? 'secondary' : 'primary'}
                                                  sx={{ p: 0.25 }}
                                                />
                                                {isOwn && (
                                                  <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                                                    own
                                                  </Typography>
                                                )}
                                              </Box>
                                            </Tooltip>
                                          );
                                        })}
                                      </Box>
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  );
                })()}
              </>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                setRoleDetailDialog({ open: false, role: null, loading: false, busyPermId: null })
              }
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* User Role Dialog */}
        <Dialog
          open={userRoleDialog.open}
          onClose={() => setUserRoleDialog({ open: false, user: null })}
          fullScreen={isMobile}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            Manage Roles for {userRoleDialog.user?.username}
          </DialogTitle>
          <DialogContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Assigned Roles</Typography>
            {userRoleDialog.user?.roles?.length > 0 ? (
              <List>
                {userRoleDialog.user.roles.map((userRole) => {
                  const role = roles.find(r => r.id === userRole.role_id);
                  return (
                    <ListItem key={userRole.id}>
                      <ListItemText
                        primary={role?.name || 'Unknown Role'}
                        secondary={`Granted: ${new Date(userRole.granted_at).toLocaleDateString()}`}
                      />
                      <ListItemSecondaryAction>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleRevokeRole(userRoleDialog.user.id, userRole.role_id)}
                        >
                          Revoke
                        </Button>
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <Typography color="text.secondary">No roles assigned</Typography>
            )}

            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Available Roles</Typography>
            <Grid container spacing={2}>
              {roles.filter(role => 
                !userRoleDialog.user?.roles?.some(ur => ur.role_id === role.id)
              ).map((role) => (
                <Grid item xs={12} sm={6} key={role.id}>
                  <Card variant="outlined">
                    <CardContent sx={{ pb: 1 }}>
                      <Typography variant="subtitle2">{role.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {role.description}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        onClick={() => handleAssignRole(userRoleDialog.user.id, role.id)}
                      >
                        Assign
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUserRoleDialog({ open: false, user: null })}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default RoleManagement;